"use client";

import { PricingTooltip } from "@components/pricing/PricingTooltip";
import Cross24 from "@ui/icons/24x24/cross-24.svg";
import Tick24 from "@ui/icons/24x24/tick-24.svg";
import { Check, X } from "lucide-react";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";

import { formatHoursAndMinutes, formatNumber } from "../format-utils";

export interface IPricingRowProps {
  align?: "center" | "end";
  className?: string;
  feature?: boolean;
  maxWidth?: boolean;
  size?: "small" | "medium" | "large";
  text?: React.ReactNode | string;
  title?: string;
  tooltip?: string;
  withPadding?: boolean;
  styleVariant?: "default" | "v3";
}

export const Row = ({
  size = "medium",
  withPadding = false,
  align = "center",
  maxWidth,
  feature,
  className,
  title,
  text,
  tooltip,
  styleVariant = "default",
}: IPricingRowProps) => {
  const isV3 = styleVariant === "v3";

  // Titles are always left-aligned (both v2 and v3), values use the provided align prop
  // For V3, values default to right-aligned
  const effectiveAlign = title ? "start" : isV3 ? "end" : align;

  const renderFeatureIcon = () => {
    if (typeof feature !== "boolean") {
      return null;
    }

    if (feature) {
      return isV3 ? <Check className='tw-w-4 tw-h-4 tw-text-green-600' /> : <Tick24 />;
    }

    return isV3 ? <X className='tw-w-4 tw-h-4 tw-text-gray-400' /> : <Cross24 />;
  };

  return (
    <div
      className={cn(
        size === "large" ? "h-84" : size === "medium" ? "h-64" : "h-44",
        withPadding && !isV3 && "px-8",
        isV3 && title ? "tw-p-2 tw-pl-0 lg:tw-p-4 lg:tw-pl-0" : isV3 ? "tw-p-2 lg:tw-p-4 tw-pr-1 lg:tw-pr-0" : "",
        className,
        "flex w-full grow items-center",
        effectiveAlign === "end" ? "ml-auto justify-end text-end" : "",
        effectiveAlign === "center" ? "justify-center" : "",
        effectiveAlign === "start" ? "justify-start text-start" : "",
      )}
    >
      {title && (
        <p
          className={cn(
            isV3 ? "tw-text-gray-700 tw-line-clamp-3 f-paragraph-03" : "f-ui-06",
            maxWidth ? "max-w-160" : "",
          )}
        >
          {title}
        </p>
      )}
      {text && <p className={isV3 ? "f-paragraph-03" : "f-description-04"}>{text}</p>}
      {tooltip && <PricingTooltip tooltip={tooltip} />}
      {renderFeatureIcon()}
    </div>
  );
};

export const FormattedNumber = ({ number }: { number?: null | number }) => {
  const t = useTranslations("PricingClientKeys.Pricing");
  return formatNumber(number) || t("unavailable");
};

export const FormattedHoursAndMinutes = ({ hoursValue }: { hoursValue: number }) => {
  const t = useTranslations("PricingClientKeys.Pricing");
  const { hours, minutes } = formatHoursAndMinutes(hoursValue);

  if (!hours && !minutes) {
    return undefined;
  }

  if (hours && minutes) {
    return t("speech_to_text.hours_and_minutes", { hours, minutes });
  }

  if (hours) {
    const key = hours === 1 ? "speech_to_text.hour" : "speech_to_text.hours";
    return t(key, { hours });
  }

  const key = minutes === 1 ? "speech_to_text.minute" : "speech_to_text.minutes";
  return t(key, { minutes });
};
